import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { styled, keyframes } from '@mui/system';

const pulseRing = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(3.5);
    opacity: 0;
  }
`;

const SensorWrapper = styled(Box)(({ top, left }) => ({
  position: 'absolute',
  top,
  left,
  transform: 'translate(-50%, -50%)',
  width: 40,
  height: 40,
}));

const SensorDot = styled(Box)(({ bgcolor }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: bgcolor,
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
}));

const SensorPulse = styled(Box)(({ bgcolor }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: bgcolor,
  transform: 'translate(-50%, -50%)',
  animation: `${pulseRing} 2s infinite`,
  opacity: 0.4,
}));

const ChartBar = styled(Box)(({ height, delay }) => ({
  width: 3,
  height,
  backgroundColor: '#fff',
  margin: '0 1px',
  animation: 'growShrink 1.2s ease-in-out infinite',
  animationDelay: delay,
  '@keyframes growShrink': {
    '0%, 100%': { transform: 'scaleY(1)' },
    '50%': { transform: 'scaleY(2)' },
  },
}));

const SensorChart = () => (
  <Box
    sx={{
      position: 'absolute',
      bottom: -8,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'end',
    }}
  >
    {[10, 14, 12].map((height, i) => (
      <ChartBar key={i} height={height} delay={`${i * 0.2}s`} />
    ))}
  </Box>
);

const sensors = [
  { top: '15%', left: '33%', color: '#4287f5', label: 'Floor 1 Room 112: 21°C' },
  { top: '20%', left: '43%', color: '#00ab33', label: 'Kitchen Calorifier: 70°C' },
  { top: '10%', left: '53%', color: '#4287f5', label: 'Floor 3 Room 303 Cold: 18°C' },
  { top: '11%', left: '63%', color: '#c40202', label: 'Floor 3 Room 303 Hot Flow: 62°C' },
  { top: '10%', left: '73%', color: '#00ab33', label: 'Floor 3 Room 303 Hot Return: 54°C' },
];

const pointAnim = keyframes`
  from { transform: translateX(10); opacity: 0; }
  to { transform: translateX(100vw); opacity: 1; }
`;

const TimeSeriesPoint = styled(Box)(({ top }) => ({
  position: 'absolute',
  top,
  left: 0,
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: '#4ade80',
  animation: `${pointAnim} 20s linear forwards`,
}));

const HeroSection = () => {
  const [points, setPoints] = useState([]);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f9fafb',
        color: 'text.primary',
        marginBottom: 10
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url('https://example.com/pipe-thermal-visual.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.1,
        }}
      />

      {/* Animated Points */}
      {points.map(point => (
        <TimeSeriesPoint key={point.id} top={point.top} />
      ))}

      {sensors.map((sensor, i) => (
        <Tooltip key={i} title={sensor.label} arrow>
        <SensorWrapper top={sensor.top} left={sensor.left}>
          <SensorPulse bgcolor={sensor.color} />
          <SensorDot bgcolor={sensor.color} />
          <SensorChart /> {/* Tiny animated chart under the sensor */}
        </SensorWrapper>
      </Tooltip>
      ))}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          px: 3,
          maxWidth: 800,
          mx: 'auto',
          mt: { xs: 20, md: 20 },
        }}
      >
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Remote Temperature Monitoring
        </Typography>
        <Typography
          variant="h4"
          component="div"
          fontWeight="bold"
          sx={{ color: 'primary.main', mb: 2 }}
        >
          Data Insights & Compliance, Delivered.
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', fontSize: '1.2rem' }}>
          Track pipework temperatures in real-time. Ensure safety, compliance, and operational efficiency—all from a single dashboard.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/*
          <Button variant="contained" color="primary" size="large">
            Get Started
          </Button>
          <Button variant="outlined" color="primary" size="large">
            See Dashboard Demo
          </Button>
          */}
        </Box>
        <Typography variant="caption" display="block" sx={{ mt: 4, color: 'text.disabled' }}>
          Trusted by engineers, facilities teams, and compliance officers across industries.
        </Typography>
      </Box>
    </Box>
  );
};

export default HeroSection;
